/**
 * Streaming Text Sanitization & Processing Utilities
 *
 * Provides utilities for sanitizing and processing text in a streaming context
 * where patterns (like source citations, markdown formatting) may arrive across
 * multiple tokens.
 *
 * Key features:
 * - Buffers potentially incomplete patterns across token boundaries
 * - Comprehensive markdown, HTML, and non-speech text stripping
 * - Word-boundary emission for optimal ElevenLabs auto_mode buffering
 * - Sentence-end detection for flush hints (improves prosody/intonation)
 */
import { sanitizeForTTSChunk } from './ttsSanitize'

/**
 * Streaming-aware text sanitizer that handles partial patterns.
 * Buffers potentially incomplete patterns and only outputs clean text.
 *
 * Handles removal of:
 * - Source citations ([Source N], (Source N), [N])
 * - Markdown formatting (bold, italic, strikethrough, headings, lists, blockquotes)
 * - Code blocks and inline code
 * - URLs and markdown links
 * - HTML tags and entities
 * - Emoji
 * - Table syntax
 */
export class StreamingSanitizer {
  private buffer = ''

  // Characters that might start a pattern to remove
  private patternStartChars = ['[', '(', '`', '*', '_', '~', '<', '&']

  /**
   * Process incoming text chunk.
   * Returns sanitized text that is safe to speak.
   * May buffer partial patterns for later processing.
   *
   * @param chunk - Incoming text chunk from stream
   * @returns Sanitized text safe to send to TTS
   */
  process(chunk: string): string {
    this.buffer += chunk

    // Find the last position where we're confident the text is complete
    // (not in the middle of a potential pattern)
    let safeEndIndex = this.buffer.length

    for (let i = this.buffer.length - 1; i >= 0; i--) {
      const char = this.buffer[i]

      if (this.patternStartChars.includes(char)) {
        const remainder = this.buffer.slice(i)

        if (this.couldBePartialPattern(remainder)) {
          safeEndIndex = i
          break
        }
      }
    }

    // Extract safe portion
    const safeText = this.buffer.slice(0, safeEndIndex)
    this.buffer = this.buffer.slice(safeEndIndex)

    // Apply full sanitization to safe portion
    return this.sanitize(safeText)
  }

  /**
   * Flush remaining buffer (call at end of stream).
   * Applies sanitization to any remaining text.
   *
   * @returns Sanitized remaining text
   */
  flush(): string {
    const remaining = this.buffer
    this.buffer = ''
    return this.sanitize(remaining)
  }

  /**
   * Reset the sanitizer state
   */
  reset(): void {
    this.buffer = ''
  }

  /**
   * Get current buffer contents (for debugging)
   */
  getBufferContents(): string {
    return this.buffer
  }

  /**
   * Check if text could be the start of a pattern we want to remove
   */
  private couldBePartialPattern(text: string): boolean {
    const partialPatterns = [
      // Source references
      // "[" could become "[Source 1]" or "[Source one]" or "[1]" — progressive match for partial "Source" keyword
      /^\[(?:S(?:o(?:u(?:r(?:c(?:e(?:s?\s*:?\s*[^[\]]*)?)?)?)?)?)?)?$/i,
      // "[Source" followed by any content without closing "]" (e.g. "[Source 1, Source 2" or "[Source one" mid-stream)
      /^\[Sources?[^\]]*$/i,
      /^\[[\d,\s]*$/,
      // "(" could become "(Source 1)" or "(Source one)" — progressive match
      /^\((?:S(?:o(?:u(?:r(?:c(?:e(?:s?\s*:?\s*[^()]*)?)?)?)?)?)?)?$/i,
      // "(Source" followed by any content without closing ")"
      /^\(Sources?[^)]*$/i,
      /^\([\d,\s]*$/,

      // Code blocks
      // "`" or "``" could become "```code```"
      /^`{1,2}$/,
      /^`{3}[^`]*$/,

      // Markdown formatting
      // "*" or "**" could become **bold** or *italic*
      /^\*{1,2}$/,
      // "_" or "__" could become __bold__ or _italic_
      /^_{1,2}$/,
      // "~" or "~~" could become ~~strikethrough~~
      /^~{1,2}$/,

      // HTML tags and entities
      // "<" could become <tag>
      /^<[a-zA-Z/]?[^>]*$/,
      // "&" could become &amp; etc.
      /^&[a-zA-Z]{0,6}$/,
      /^&#?\d{0,4}$/,
    ]

    return partialPatterns.some((p) => p.test(text))
  }

  /**
   * Apply comprehensive text sanitization for TTS.
   * Delegates to the canonical rule set in ./ttsSanitize. Uses the
   * untrimmed sanitizeForTTSChunk, not sanitizeForTTS: this runs once per
   * incoming stream chunk, and a trimmed whitespace-only chunk would glue
   * adjacent words together when StreamingTextProcessor concatenates
   * results (phraseBuffer += cleaned). The overall streamed output is still
   * trimmed once, in StreamingTextProcessor.flush().
   */
  private sanitize(text: string): string {
    return sanitizeForTTSChunk(text)
  }
}

/**
 * Processed text output from StreamingTextProcessor.
 */
export interface ProcessedText {
  /** Cleaned text to send to ElevenLabs (typically ends with space) */
  text: string
  /** Whether to flush at this point (sentence end detected — improves prosody) */
  flush: boolean
}

/**
 * Streaming text processor for TTS.
 *
 * Phrase-boundary emission strategy designed to maximize prosody quality
 * while maintaining low latency. Each emitted phrase contains enough
 * context (25-200 chars) for ElevenLabs to produce natural intonation.
 *
 * Processing pipeline:
 * 1. Sanitizes incoming tokens (comprehensive markdown/HTML/citation stripping)
 * 2. Buffers to phrase boundaries (sentence ends, clause boundaries, or max length)
 * 3. Emits coherent phrases as single WebSocket messages
 * 4. Sets flush=true at sentence endings for proper sentence-final intonation
 *
 * Combined with ElevenLabs auto_mode + chunk_length_schedule, this gives
 * the TTS model full phrasal context for natural prosody and intonation.
 */
export class StreamingTextProcessor {
  private sanitizer: StreamingSanitizer
  private phraseBuffer = ''

  /** Minimum chars before emitting at a phrase boundary */
  private static readonly MIN_PHRASE_LENGTH = 25
  /** Force emit at word boundary if buffer exceeds this */
  private static readonly MAX_PHRASE_LENGTH = 200

  constructor() {
    this.sanitizer = new StreamingSanitizer()
  }

  /**
   * Process incoming token and return phrases ready for TTS.
   *
   * Buffers cleaned text until a natural phrase boundary is reached
   * (sentence end, clause boundary, or max length). This ensures each
   * WebSocket message contains a coherent phrase with enough context
   * for ElevenLabs to produce natural prosody and intonation.
   *
   * Sets flush=true at sentence endings so ElevenLabs generates audio
   * with proper sentence-final intonation (falling pitch, etc.).
   *
   * @param token - Incoming token from LLM stream
   * @returns Array of processed phrases ready to send (may be empty)
   */
  processToken(token: string): ProcessedText[] {
    const cleaned = this.sanitizer.process(token)
    if (!cleaned) return []

    this.phraseBuffer += cleaned
    return this.extractPhrases()
  }

  /**
   * Extract complete phrases from the buffer.
   * Keeps extracting while the buffer contains a suitable boundary
   * at or past the minimum phrase length.
   */
  private extractPhrases(): ProcessedText[] {
    const results: ProcessedText[] = []

    while (this.phraseBuffer.length >= StreamingTextProcessor.MIN_PHRASE_LENGTH) {
      const boundary = this.findBestBoundary()
      if (!boundary) break

      const phrase = this.phraseBuffer.slice(0, boundary.index)
      this.phraseBuffer = this.phraseBuffer.slice(boundary.index)

      if (phrase.trim()) {
        results.push({ text: phrase, flush: boundary.flush })
      }
    }

    return results
  }

  /**
   * Find the best phrase boundary in the buffer.
   *
   * Priority order:
   * 1. Sentence end (.!? + optional quotes/parens + whitespace) — flush=true
   * 2. Clause boundary (,;:—– + whitespace) — flush=false
   * 3. Max-length forced break at word boundary — flush=false
   *
   * Returns null if no suitable boundary is found yet (buffer too short).
   */
  private findBestBoundary(): { index: number; flush: boolean } | null {
    const buf = this.phraseBuffer
    const min = StreamingTextProcessor.MIN_PHRASE_LENGTH
    const max = StreamingTextProcessor.MAX_PHRASE_LENGTH

    // Priority 1: Sentence end — triggers flush for sentence-final intonation
    const sentenceEndRegex = /[.!?]["'\u201D\u2019)]*\s/g
    let match: RegExpExecArray | null
    while ((match = sentenceEndRegex.exec(buf)) !== null) {
      const idx = match.index + match[0].length
      if (idx >= min) {
        return { index: idx, flush: true }
      }
    }

    // Priority 2: Clause boundary (comma, semicolon, colon, em/en dash + space)
    const clauseRegex = /[,;:\u2014\u2013]\s/g
    while ((match = clauseRegex.exec(buf)) !== null) {
      const idx = match.index + match[0].length
      if (idx >= min) {
        return { index: idx, flush: false }
      }
    }

    // Priority 3: Force break at word boundary if past max length
    if (buf.length >= max) {
      const lastSpace = buf.lastIndexOf(' ', max)
      if (lastSpace > 0) {
        return { index: lastSpace + 1, flush: false }
      }
      // No space found (very long word), break at max
      return { index: max, flush: false }
    }

    return null
  }

  /**
   * Flush remaining buffer (call at end of stream).
   * Returns final text to send.
   *
   * @returns Final text chunk (may be empty string)
   */
  flush(): string {
    // Flush sanitizer first to get any buffered partial patterns
    const remaining = this.sanitizer.flush()
    this.phraseBuffer += remaining

    const text = this.phraseBuffer.trim()
    this.phraseBuffer = ''

    if (text) {
      return text.endsWith(' ') ? text : text + ' '
    }
    return ''
  }

  /**
   * Reset the processor state
   */
  reset(): void {
    this.phraseBuffer = ''
    this.sanitizer.reset()
  }

  /**
   * Get current buffer contents (for debugging)
   */
  getBufferContents(): string {
    return this.phraseBuffer + this.sanitizer.getBufferContents()
  }
}
